import { useEffect, useRef, useState } from 'react';

import {
  type PreviewMode,
  readModePref,
  usePreviewAudio,
  writeAudioPref,
  writeModePref,
} from '../lib/previewMode';
import { HlsPreview } from './HlsPreview';
import { WhepPreview } from './WhepPreview';

interface PreviewPlayerProps {
  /** WHEP endpoint for the sub-second WebRTC preview (`app=live`).
   *  Carries Opus audio as well as video — SRS transcodes the
   *  publisher's AAC in its `rtmp_to_rtc` bridge. Null disables WebRTC. */
  webrtcUrl: string | null;
  /** HLS playlist for the fallback preview. Null disables HLS. */
  hlsUrl: string | null;
  /** Re-attach sources when this changes (stream-key swap, etc.). */
  resetKey?: string;
  className?: string;
}

/**
 * Wraps `HlsPreview` + `WhepPreview` with two small toggles:
 *
 *   - **Transport**: WebRTC (<1 s, needs UDP egress) vs HLS (3-4 s,
 *     works through any HTTPS-only network). Rendered as a segmented
 *     control so it's obvious they're a connected pair of options.
 *   - **Audio** (both transports, persisted and shared via
 *     `lib/previewMode`): play the correspondent's audio. HLS carries
 *     the publisher's AAC as-is; WebRTC gets Opus, which SRS transcodes
 *     from that AAC inside its `rtmp_to_rtc` bridge. The video element
 *     starts muted so autoplay isn't blocked by browser policy; the
 *     click that turns audio on is itself the user gesture the browser
 *     needs to allow unmuted playback. On WebRTC, turning audio on
 *     reconnects with an audio track in the offer — with it off the
 *     subscription stays video-only.
 *
 * Defaults: transport HLS, audio off. HLS is the safer first-render
 * choice — it works through any HTTPS-only network without UDP egress,
 * so operators on locked-down networks see a working preview straight
 * away. Audio off so the green-room isn't suddenly loud on first load.
 * Both preferences persist via localStorage.
 */
export function PreviewPlayer({
  webrtcUrl,
  hlsUrl,
  resetKey,
  className,
}: PreviewPlayerProps): JSX.Element | null {
  const [mode, setMode] = useState<PreviewMode>(() => {
    const pref = readModePref();
    // Fall back if the persisted choice can't actually be honoured
    // (this card only carries one of the two URLs).
    if (pref === 'webrtc' && !webrtcUrl) return 'hls';
    if (pref === 'hls' && !hlsUrl) return webrtcUrl ? 'webrtc' : 'hls';
    return pref;
  });
  // Page-wide, not per-card: every preview card and the talkback hint
  // read the same preference, so turning audio on anywhere turns it on
  // everywhere and the hint never contradicts what the operator hears.
  const audio = usePreviewAudio();

  // Fullscreen the whole player container (not just the raw video), so
  // the transport / audio toggles ride along and the operator can still
  // flip WebRTC↔HLS or unmute without leaving fullscreen. Tracks the
  // browser's actual fullscreen element via the `fullscreenchange`
  // event so pressing Esc syncs our icon state back to "enter" without
  // us having to know about the key press.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) {
      document.exitFullscreen().catch(() => {
        /* browser refused — nothing sensible to surface */
      });
    } else {
      el.requestFullscreen().catch(() => {
        /* Firefox on non-secure origins, iframe without allow=fullscreen,
         * or user denial. Silent — the button just doesn't do anything. */
      });
    }
  };

  // Persist preferences whenever they change so operator choices
  // survive page reloads and propagate across other open green-room
  // tabs via the `storage` event.
  useEffect(() => {
    writeModePref(mode);
  }, [mode]);

  if (!webrtcUrl && !hlsUrl) return null;

  const showTransportToggle = webrtcUrl != null && hlsUrl != null;
  const active = mode === 'webrtc' && webrtcUrl ? 'webrtc' : 'hls';

  return (
    <div
      ref={containerRef}
      className={`space-y-1.5 ${
        // When the browser puts us into fullscreen, `space-y-1.5` on the
        // wrapper plus intrinsic video sizing leave the video floating
        // in the middle of a black backdrop. Switch to a column flex
        // that pushes the toolbar to the top and lets the preview
        // consume the remaining height. Only applied while actually
        // fullscreen so the normal card layout is untouched.
        isFullscreen ? 'flex h-full w-full flex-col bg-black p-3' : ''
      } ${className ?? ''}`}
    >
      {/* Toolbar is always rendered because the fullscreen button is
       *  always available; the transport / audio controls appear as
       *  applicable. */}
      <div className="flex flex-wrap items-center justify-end gap-2 text-[10px] font-medium uppercase tracking-wide">
        {showTransportToggle && (
          // Segmented control: shared border + internal divider so
          // it reads as one switch with two states, not two
          // unrelated buttons. The active half fills with the
          // foreground colour; the inactive half stays low-contrast
          // but with a hover lift so the affordance is obvious.
          <div className="inline-flex overflow-hidden rounded-md border border-line-hair">
            <button
              type="button"
              onClick={() => setMode('webrtc')}
              className={`px-2 py-0.5 transition-colors ${
                active === 'webrtc'
                  ? 'bg-foreground text-background'
                  : 'bg-transparent text-muted-foreground hover:bg-surface-muted hover:text-ink'
              }`}
              aria-pressed={active === 'webrtc'}
            >
              WebRTC · &lt;1 s
            </button>
            <button
              type="button"
              onClick={() => setMode('hls')}
              className={`border-l border-line-hair px-2 py-0.5 transition-colors ${
                active === 'hls'
                  ? 'bg-foreground text-background'
                  : 'bg-transparent text-muted-foreground hover:bg-surface-muted hover:text-ink'
              }`}
              aria-pressed={active === 'hls'}
            >
              HLS · 3-4 s
            </button>
          </div>
        )}
        {/* Stand-alone pill: same border treatment as the segmented
         *  control but reads as an independent on/off switch. Shown on
         *  both transports — each carries the correspondent's audio. */}
        <button
          type="button"
          onClick={() => writeAudioPref(!audio)}
          className={`rounded-md border px-2 py-0.5 transition-colors ${
            audio
              ? 'border-foreground bg-foreground text-background'
              : 'border-line-hair bg-transparent text-muted-foreground hover:bg-surface-muted hover:text-ink'
          }`}
          aria-pressed={audio}
          title={audio ? "Audio on — you hear the correspondent" : 'Audio off — silent video preview'}
        >
          {audio ? 'Audio on' : 'Audio off'}
        </button>
        {/* Fullscreen toggle. Requests fullscreen on the whole player
         *  container (not just the video) so the toolbar rides along
         *  and the operator can still switch transport / toggle audio
         *  from inside fullscreen. Enter shows ⛶, exit shows ⛶ with
         *  filled treatment. Kept as its own pill (not part of the
         *  segmented control) because it's a mode toggle, not an
         *  option choice. */}
        <button
          type="button"
          onClick={toggleFullscreen}
          className={`rounded-md border px-2 py-0.5 transition-colors ${
            isFullscreen
              ? 'border-foreground bg-foreground text-background'
              : 'border-line-hair bg-transparent text-muted-foreground hover:bg-surface-muted hover:text-ink'
          }`}
          aria-pressed={isFullscreen}
          title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
        >
          <span aria-hidden className="text-sm leading-none">
            ⛶
          </span>
          <span className="sr-only">{isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}</span>
        </button>
      </div>
      {/* In fullscreen the video would otherwise want to be
       *  `w-full × 56.25 %` — on a 1920×1080 display with a ~40 px
       *  toolbar that overflows the viewport by the toolbar's height.
       *
       *  Layered fullscreen sizing (all via arbitrary descendant
       *  selectors — the preview components' outer div isn't targetable
       *  from here otherwise):
       *    1. Our wrapper: `flex-1 min-h-0` so it eats the remaining
       *       vertical space under the toolbar (min-h-0 lets a flex
       *       child shrink below its content's intrinsic height).
       *    2. Their outer `<div>` (`[&>div]`): flex + full size so it
       *       hands the video a real rectangle to fill.
       *    3. Their `<video>` (`[&_video]`): `!aspect-auto` cancels the
       *       component's own `aspect-video`, then `max-h/w-full` +
       *       `h/w-auto` lets the intrinsic 16:9 shrink to fit the
       *       rectangle with proper letterboxing.
       *
       *  Outside fullscreen the wrapper is a plain pass-through and the
       *  `aspect-video` on the video governs sizing as before. */}
      <div
        className={
          isFullscreen
            ? 'flex min-h-0 flex-1 items-center justify-center [&>div]:flex [&>div]:h-full [&>div]:w-full [&>div]:items-center [&>div]:justify-center [&_video]:!aspect-auto [&_video]:h-auto [&_video]:max-h-full [&_video]:w-auto [&_video]:max-w-full'
            : ''
        }
      >
        {active === 'webrtc' && webrtcUrl ? (
          <WhepPreview src={webrtcUrl} resetKey={resetKey} audio={audio} />
        ) : (
          <HlsPreview src={hlsUrl} resetKey={resetKey} audio={audio} />
        )}
      </div>
    </div>
  );
}
